/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import SelectPicker from 'rsuite/SelectPicker';
import { Tooltip } from '@mui/material';
import { Settings } from '@mui/icons-material';
import { BarChart3, LayoutList, Sheet, Waypoints, Utensils, Maximize, Salad, Calendar, Minimize, Box, Store, Eye } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import FlowChart from './FlowChart/FlowChart';
import styles from './ProcessFlowPage.module.scss';
import view from '@/Images/view.png';
import issued from '@/Images/issued.svg';
import produced from '@/Images/produced.svg';
import internalDispatch from '@/Images/internal dispatch.svg';
import dispatch from '@/Images/externalDispatch.svg';
import defaultMode from '@/Images/DefaultMode.svg';
import wastage from '@/Images/wastage.svg';
import ega from '@/Images/ega.svg'
import yeildLossActual from '@/Images/yeildLossActual.png';
import yeildLossExpected from '@/Images/yeildLossExpected.png';
import primaryPack from '@/Images/primaryPack.svg';
import secondaryPack from '@/Images/secondaryPack.svg';
import restaurantIssue from '@/Images/res_issued.svg';
import { urls } from '@/Constants';
import FilterNodes from './FilterNodes';
import Legend from '@/Components/Legend/Legend';
import DrillDownButtons from '@/Components/DrillDownButtons/DrillDownButtons';
import { useDispatch, useSelector } from 'react-redux';
import { modalActions } from '@/Actions';
import QRCode from 'qrcode';
import inventoryAnalysisIcon from '@/Images/inventoryAnalysis.png';
import BatchPerformanceHandler from '../AI FMS Summary  Dyanmic/Component/BatchPerformanceHandler';
import moment from 'moment';


const BUTTON_LIST = [
    { label: 'Actual vs Planned', icon: <Waypoints size={16} />, value: 'sectionDetails' },
    { label: 'Product Batch Journey', icon: <Utensils size={16} />, value: 'productJourney', placeholder: 'Select Product' },
];

const LEGEND_LIST = [
    { label: 'Issuance', value: 'issuance', img: issued, showEye: true },
    { label: 'Transfer In', value: 'procured', img: internalDispatch, showEye: true },
    { label: 'Transfer Out', value: 'transfer out', icon: <img src={internalDispatch} alt='Transfer Out' className='w-5 h-5 -scale-x-100' />, showEye: true },
    { label: 'Produced', value: 'produced', img: produced, showEye: true },
    { label: 'Dispatch', value: 'dispatch', icon: <img src={dispatch} alt='Dispatch' className='w-5 h-5 -scale-x-100' />, showEye: true },
    { label: 'Wastage', value: 'wastage', img: wastage, showEye: true },
    { label: 'EGA', value: 'ega', img: ega, showEye: true },
    { label: 'Yield Loss Actual', value: 'yieldLossActual', img: yeildLossActual, showEye: true },
    { label: 'Yield Loss Expected', value: 'yieldLossExpected', img: yeildLossExpected, showEye: true },
    { label: 'Primary Pack', value: 'primary', img: primaryPack, showEye: true },
    { label: 'Secondary Pack', value: 'secondary', img: secondaryPack, showEye: true },
    { label: 'Restaurant', value: 'restaurant', img: restaurantIssue, showEye: true },
    { label: 'Default Mode', value: 'defaultMode', img: defaultMode, showEye: true },
];

// Helper function to filter display values by legend(s)
// Creates a mapping from legend value to possible display key patterns
const LEGEND_KEY_MAPPING = {
    'procured': ['procured', 'issuance', 'issued'],
    'produced': ['produced', 'production'],
    'dispatch': ['dispatch', 'dispatched'],
    'wastage': ['wastage', 'waste'],
    'ega': ['ega'],
    'yieldlossactual': ['yeildlossactual', 'yieldlossactual', 'yield loss actual'],
    'yieldlossexpected': ['yeildlossexpected', 'yieldlossexpected', 'yield loss expected'],
    'primary': ['primary', 'primarypackaged', 'primary packaging', 'primary pack'],
    'secondary': ['secondary', 'secondarypackaged', 'secondary packaging', 'secondary pack'],
    'restaurant': ['restaurant', 'restaurantissued', 'restaurant issue', 'indent'],
    'defaultmode': ['defaultmode', 'default mode'],
};

const filterDisplayValueByLegend = (displayValue, legendValues) => {
    if (!legendValues) return displayValue;

    const legendsToHide = Array.isArray(legendValues) ? legendValues : [legendValues];
    if (legendsToHide.length === 0) return displayValue;

    const filtered = {};

    Object.keys(displayValue || {}).forEach((key) => {
        const keyLower = key?.trim()?.toLowerCase()?.replace(/\s+/g, '');

        const shouldHide = legendsToHide.some(legendValue => {
            const legendLower = legendValue?.toLowerCase();
            const patterns = LEGEND_KEY_MAPPING[legendLower] || [legendLower];

            return patterns.some(pattern => {
                const patternNormalized = pattern.replace(/\s+/g, '');
                return keyLower.includes(patternNormalized) || patternNormalized.includes(keyLower);
            });
        });

        if (!shouldHide) {
            filtered[key] = displayValue[key];
        }
    });

    return filtered;
};

const getNodeScore = (node) => {
    const data = node?.data || {};
    let score = 0;
    const metrics = data?.stats?.displayValue || {};
    score += Object.keys(metrics).length * 10;
    if (data?.asset?.assetName) score += 5;
    if (data?.defaultMode) score += 5;
    if (data?.totalMachines > 0) score += 5;
    if (data?.pending >= 0 || data?.completed >= 0) score += 3;
    if (data?.orderId || data?.wholeApi?.procurePoSummary || data?.wholeApi?.dispatchPoSummary) score += 3;
    return score;
};

const ProcessFlowMiddleContainer = ({
    retailStores,
    retailSelectedStore,
    setRetailSelectedStore,
    outletsFulfillmentObj,
    getProgressBarColor,
    selectedRetailOrderStats,
    showOutletData,
    toggleProcessModal,
    isInternal,
    isEdit,
    initialState,
    handleNodesState,
    poActionHandler,
    setIsChanged,
    toggleImageModal,
    toggleEditNodeModal,
    toggleAlert,
    handleEdgeDelete,
    handleNodeDelete,
    selectedStore,
    state,
    level,
    toggleProcessSetting,
    BASE_STORES,
    setInitialState,
    isChanged,
    immovableAssetsList,
    handleCancel,
    handleSave,
    setProductId,
    handleFullMode,
    fullMode = false,
    selectedBatches,
    setSelectedBatches,
    setState = () => { },
    setSelectedShift = () => { },
    setIsProductJourneyActive = () => { }
}) => {
    const productList = useSelector((state) => state.form.ALL_PRODUCTS.data.LIST);
    const [isLegendOpen, setIsLegendOpen] = useState(false);
    const [drillDownView, setDrillDownView] = useState({
        label: 'Actual vs Planned',
        icon: <Waypoints size={16} />,
        value: 'sectionDetails',
        show: true,
        data: []
    });
    const [selectedLegend, setSelectedLegend] = useState(['defaultMode']);
    const [isBatchPerformanceActive, setIsBatchPerformanceActive] = useState(false);
    const [viewMode, setViewMode] = useState('simple'); // 'graph' or 'simple'
    const [overrideDateRange, setOverrideDateRange] = useState(null);
    const dispatch = useDispatch();
    const legendRef = useRef(null);

    const filteredState = useMemo(() => {
        if (!selectedLegend) return initialState;
        const legendsToHide = Array.isArray(selectedLegend) ? selectedLegend : [selectedLegend];
        const hasDefaultMode = legendsToHide.includes('defaultMode');

        return {
            ...initialState,
            nodes: initialState?.nodes?.map((node) => ({
                ...node,
                data: {
                    ...node?.data,
                    ...(hasDefaultMode && { defaultMode: null }),
                    stats: {
                        ...node?.data?.stats,
                        displayValue: filterDisplayValueByLegend(node?.data?.stats?.displayValue, selectedLegend)
                    }
                }
            }))
        };
    }, [initialState, selectedLegend]);

    const productJourneyData = useMemo(() => {
        return productList
            ?.filter((product) => product?.skuType === 'PRODUCT' || product?.skuType === 'UNPACKED_PRODUCT')
            ?.flatMap((product) =>
                product?.variants?.map((variant) => ({
                    value: product?.id,
                    label: `${product?.productName}${variant?.displayName ? ` (${variant?.displayName})` : ''}${variant?.clientVariantId ? ` (${variant?.clientVariantId})` : ''}`,
                }))
            ) || [];
    }, [productList]);

    const sectionDetailsData = useMemo(() => {
        return Object.values(
            initialState?.nodes?.reduce((acc, node) => {
                const label = node.data.sectionName ?? '';
                if (!acc[label]) {
                    acc[label] = { label, value: label };
                }
                return acc;
            }, {}) || {}
        );
    }, [initialState?.nodes]);

    const handleLegendChange = useCallback((legendValue) => {
        setSelectedLegend(legendValue);
    }, []);

    const handleDrillDownView = useCallback((obj) => {
        setSelectedLegend(['defaultMode']);
        if (obj?.value === 'productJourney') {
            setIsBatchPerformanceActive(true);
            setIsProductJourneyActive(true);
        }
        if (obj?.value === 'sectionDetails') {
            setIsProductJourneyActive(false);
            setProductId(null);
            setSelectedBatches([]);
            setState((prev) => ({
                ...prev,
                selectedTimeType: 'WORK_ORDER',
            }));
        }
        const data = obj?.value === 'sectionDetails' ? [] : []; // Force empty for sectionDetails to avoid dropdown
        setDrillDownView({
            ...obj,
            show: true,
            placeholder: obj?.placeholder,
            selectedValue: null,
            data,
        });
    }, [sectionDetailsData, productJourneyData]);

    const handleValueDrillDownView = useCallback((obj) => {
        if (obj?.value === 'productJourney') {
            setProductId(obj?.selectedValue);
            setSelectedBatches([]);
        }
        setDrillDownView(obj);
    }, [setSelectedBatches]);

    useEffect(() => {
        if (!isLegendOpen) return;
        const handleClickOutside = (event) => {
            if (legendRef.current && !legendRef.current.contains(event.target)) {
                setIsLegendOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isLegendOpen]);

    const filterNodes = useMemo(() => {
        let nodes = initialState?.nodes || [];
        if (drillDownView?.selectedValue && drillDownView?.value === 'sectionDetails') {
            nodes = nodes.filter((node) => node?.data?.sectionName === drillDownView?.selectedValue);
        } else if (drillDownView?.value === 'productJourney') {
            nodes = nodes.filter((node) => Object.values(node?.data?.stats?.displayValue)?.length !== 0);
        }
        if (selectedLegend) {
            // Convert to array for consistent handling
            const legendsToHide = Array.isArray(selectedLegend) ? selectedLegend : [selectedLegend];
            const hasDefaultMode = legendsToHide.includes('defaultMode');

            nodes = nodes.map((node) => ({
                ...node,
                data: {
                    ...node?.data,
                    ...(hasDefaultMode && { defaultMode: null }),
                    stats: {
                        ...node?.data?.stats,
                        displayValue: filterDisplayValueByLegend(node?.data?.stats?.displayValue, selectedLegend)
                    }
                }
            }));
        }

        return nodes.map((node) => ({ ...node, score: getNodeScore(node) }));
    }, [initialState?.nodes, drillDownView?.selectedValue, drillDownView?.value, selectedLegend, selectedBatches, isBatchPerformanceActive]);

    const filterNodeHandlers = useMemo(() => ({
        toggleProcessModal,
        poActionHandler,
        handleAssetView: async (item) => {
            try {
                const qrDataURL = await QRCode.toDataURL(item?.asset?.assetQrCode, { width: 500, height: 400 });
                toggleImageModal(true, {
                    image: qrDataURL,
                    width: '50rem',
                    height: '50rem',
                    heading: `${item?.asset?.assetName} (${item?.asset?.assetTypeName})`,
                    centerImage: true,
                });
            } catch (error) {
                console.error('QR Code generation failed:', error);
            }
        },
        handleAssetUpdate: (item) => {
            toggleEditNodeModal(true, {
                heading: item?.heading,
                sectionId: item?.sectionId,
                asset: item?.asset?.id,
                type: item?.type,
                defaultMode: item?.defaultMode,
                id: item?.id,
                nodeStore: item?.nodeStore,
            });
        },
        onDelete: handleNodeDelete,
    }), [toggleProcessModal, poActionHandler, toggleImageModal, toggleEditNodeModal, handleNodeDelete]);

    const productionAnalysisUrl = useMemo(() => {
        const dateFilter = encodeURIComponent(JSON.stringify({ from: state?.selectedDate, to: state?.selectedDate }));
        return `${urls.EXTENSION}${urls.PRODUCTION_ANALYSIS}?tab=geographyWise&timeFilter=store&dateRangeFilter=${dateFilter}`;
    }, [state?.selectedDate]);

    const operationsSummaryUrl = useMemo(() => {
        const dateFilter = encodeURIComponent(JSON.stringify({ from: state?.selectedDate, to: state?.selectedDate }));
        return `${urls.EXTENSION}${urls.AIFMS_SUMMARY_DYNAMIC}?tab=geographyWise&timeFilter=store&dateRangeFilter=${dateFilter}`;
    }, [state?.selectedDate]);

    const inventoryAnalysisUrl = useMemo(() => {
        return `${urls.EXTENSION}${urls.INV_ANALYSIS}`;
    }, []);
    const longTermProductionPlansUrl = useMemo(() => {
        return `${urls.EXTENSION}${urls.PRODUCTION_PLANS}?openModal=true`;
    }, []);

    const toggleLegend = useCallback(() => setIsLegendOpen(prev => !prev), []);

    const openProductionAnalysis = useCallback(() => window.open(productionAnalysisUrl, '_blank'), [productionAnalysisUrl]);
    const openOperationsSummary = useCallback(() => window.open(operationsSummaryUrl, '_blank'), [operationsSummaryUrl]);
    const openInventoryAnalysis = useCallback(() => window.open(inventoryAnalysisUrl, '_blank'), [inventoryAnalysisUrl]);
    const openLongTermProductionPlans = useCallback(() => window.open(longTermProductionPlansUrl, '_blank'), [longTermProductionPlansUrl]);

    const handleSettingsClick = useCallback(() => {
        toggleProcessSetting(true, {
            storeId: selectedStore,
            baseStores: BASE_STORES,
            setInitialState,
            isChanged,
            setIsChanged,
            immovableAssetsList,
        });
    }, [toggleProcessSetting, selectedStore, BASE_STORES, setInitialState, isChanged, setIsChanged, immovableAssetsList]);

    const hasRetailStats = Object.keys(selectedRetailOrderStats?.statsSummary || {}).length > 0;
    // Dispatch = fulfilled %, Receiving = receiving %.
    const dispatchPercent = Number.isNaN(outletsFulfillmentObj?.fulfillmentPercent) ? 0 : (outletsFulfillmentObj?.fulfillmentPercent ?? 0);
    const receivingPercent = Number.isNaN(outletsFulfillmentObj?.receivingPercent) ? 0 : (outletsFulfillmentObj?.receivingPercent ?? 0);
    // Raw cost (₹) amounts — bars ke saath dikhane ke liye.
    const orderedCost = Number(selectedRetailOrderStats?.statsSummary?.ordered || 0);
    const dispatchedCost = Number(selectedRetailOrderStats?.statsSummary?.fulfilled || 0);
    const receivedCost = Number(selectedRetailOrderStats?.statsSummary?.received || 0);
    const fmtINR = (n) => {
        const v = Number(n) || 0;
        if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
        if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
        if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
        return `₹${v.toFixed(0)}`;
    };
    return (
        <div
            className={`relative ${(fullMode || viewMode !== 'simple') ? 'overflow-auto' : ''}`}
            style={{
                // Normal cards (simple) view grows with its content so the page scrolls
                // as a single scroll — no nested inner scrollbar. FlowChart and
                // fullscreen keep a fixed-height scroll region (the canvas needs a height).
                height: fullMode
                    ? '90vh'
                    : viewMode === 'simple'
                        ? 'auto'
                        : 'calc(100vh - 290px)',
            }}
        >
            {/* Distribution card - bottom-right (empty area). z-[1000] taaki cards iske
                peeche rahein, upar na aayein. fullMode (fullscreen) me chhup jaata hai. */}
            {!fullMode && (
                <div className='absolute bottom-8 right-4 z-[1000] w-[200px] bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden'>
                    {/* Header */}
                    <div className='flex items-center justify-between px-2 py-1 bg-gradient-to-r from-[#4f3864] to-[#6d4b86] text-white'>
                        <div className='flex items-center gap-1'>
                            <Store size={13} />
                            <span className='text-[11px] font-semibold tracking-wide'>Distribution</span>
                        </div>
                        {hasRetailStats && (
                            <Tooltip title='View outlet receiving details'>
                                <button
                                    type='button'
                                    onClick={showOutletData}
                                    className='flex items-center gap-0.5 text-white/90 hover:text-white text-[10px] font-medium bg-white/15 hover:bg-white/25 transition-colors rounded px-1.5 py-0.5'
                                >
                                    <Eye size={11} /> Details
                                </button>
                            </Tooltip>
                        )}
                    </div>
                    {/* Body */}
                    <div className='p-2 flex flex-col gap-1.5'>
                        <SelectPicker
                            data={retailStores}
                            value={retailSelectedStore}
                            onChange={setRetailSelectedStore}
                            placeholder='Select Outlet'
                            placement='topStart'
                            className='w-full'
                            cleanable={false}
                            size='xs'
                            menuClassName='z-[100000]'
                            menuStyle={{ zIndex: 100000 }}
                        />
                        {/* Dispatch (fulfilled) */}
                        <div className='flex flex-col gap-0.5'>
                            <div className='flex items-center gap-1.5'>
                                <span className='text-[10px] font-medium text-gray-500 whitespace-nowrap w-[64px]'>Dispatch</span>
                                <div className='h-1.5 flex-1 rounded-full bg-gray-100 overflow-hidden'>
                                    <div
                                        className='h-full rounded-full transition-all duration-500'
                                        style={{
                                            width: `${dispatchPercent}%`,
                                            backgroundColor: getProgressBarColor(dispatchPercent),
                                        }}
                                    />
                                </div>
                                <span className='text-[11px] font-bold w-8 text-right' style={{ color: getProgressBarColor(dispatchPercent) }}>
                                    {dispatchPercent}%
                                </span>
                            </div>
                            <span className='text-[9px] text-gray-400 text-right'>
                                {fmtINR(dispatchedCost)} / {fmtINR(orderedCost)}
                            </span>
                        </div>
                        {/* Receiving */}
                        <div className='flex flex-col gap-0.5'>
                            <div className='flex items-center gap-1.5'>
                                <span className='text-[10px] font-medium text-gray-500 whitespace-nowrap w-[64px]'>Receiving</span>
                                <div className='h-1.5 flex-1 rounded-full bg-gray-100 overflow-hidden'>
                                    <div
                                        className='h-full rounded-full transition-all duration-500'
                                        style={{
                                            width: `${receivingPercent}%`,
                                            backgroundColor: getProgressBarColor(receivingPercent),
                                        }}
                                    />
                                </div>
                                <span className='text-[11px] font-bold w-8 text-right' style={{ color: getProgressBarColor(receivingPercent) }}>
                                    {receivingPercent}%
                                </span>
                            </div>
                            <span className='text-[9px] text-gray-400 text-right'>
                                {fmtINR(receivedCost)} / {fmtINR(orderedCost)}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Top Left Controls */}
            <div className='flex absolute flex-col gap-2 justify-start items-start gap-2 top-[0.5rem] left-[2rem] z-30'>
                <Tooltip title='Go to Production Analysis'>
                    <div onClick={openProductionAnalysis} className='px-3 py-2 bg-yellow-400 rounded-md flex items-center gap-2 cursor-pointer hover:bg-yellow-500 transition-colors shadow-md'>
                        <BarChart3 size={18} strokeWidth={1.5} />
                    </div>
                </Tooltip>
                <Tooltip title='Go to Inventory Analysis'>
                    <div onClick={openInventoryAnalysis} className='px-3 py-2 bg-yellow-400 rounded-md flex items-center gap-2 cursor-pointer hover:bg-yellow-500 transition-colors shadow-md'>
                        <img src={inventoryAnalysisIcon} className='h-4 w-4 scale-150' />
                    </div>
                </Tooltip>
                <Tooltip title='Go to Operations Summary (Beta)'>
                    <div onClick={openOperationsSummary} className='px-3 py-2 bg-yellow-400 rounded-md flex items-center gap-2 cursor-pointer hover:bg-yellow-500 transition-colors shadow-md'>
                        <Sheet size={18} strokeWidth={1.5} />
                    </div>
                </Tooltip>
                <Tooltip title='Create Long term Production Plan'>
                    <div onClick={openLongTermProductionPlans} className='px-3 py-2 bg-yellow-400 rounded-md flex items-center gap-2 cursor-pointer hover:bg-yellow-500 transition-colors shadow-md'>
                        <Calendar size={18} strokeWidth={1.5} />
                    </div>
                </Tooltip>
                <div ref={legendRef} className='relative'>
                    <div onClick={toggleLegend} className='px-3 py-2 border border-black rounded-md flex items-center gap-2 cursor-pointer shadow-md bg-white'>
                        <LayoutList size={18} strokeWidth={1.5} />
                    </div>
                    {isLegendOpen && (
                        <Legend list={LEGEND_LIST} setIsLegend={handleLegendChange} defaultValue={['defaultMode']} isClickable selectedLegend={selectedLegend} multiSelect={true} />
                    )}
                </div>
            </div>
            <div className='absolute flex justify-start items-start gap-2 top-[0.5rem] left-[5rem] z-30'>
                <DrillDownButtons
                    buttonList={BUTTON_LIST}
                    drillDownView={drillDownView}
                    setDrillDownView={handleValueDrillDownView}
                    onButtonClick={handleDrillDownView}
                    virtulized
                    isTransparent={!drillDownView?.show}
                    hideBack
                >
                    {drillDownView?.value === 'productJourney' && (
                        <div className='flex items-center gap-2 ml-2'>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsBatchPerformanceActive(true);
                                }}
                                className='flex px-3 text-black text-[11px] font-bold py-2 items-center gap-2 bg-yellow-400 hover:bg-yellow-500 transition-colors shadow-md rounded-md uppercase tracking-wider'
                            >
                                <LayoutList size={14} />
                                Select Batch
                            </button>

                            {selectedBatches?.length > 0 && (() => {
                                const batch = selectedBatches[0];
                                return (
                                    <div className='flex items-center gap-2 p-1 bg-white rounded-lg border border-indigo-200 ml-2 shadow-sm animate-in fade-in slide-in-from-left-2 duration-300'>
                                        <div className='flex items-center gap-2 px-2 border-r border-indigo-100 shrink-0'>
                                            <Box size={14} className='text-indigo-600' />
                                            <span className='text-[10px] font-bold text-indigo-700 uppercase tracking-tight'>
                                                Batch Selected
                                            </span>
                                        </div>
                                        <div className='flex items-center gap-3 px-2 py-0.5'>
                                            <div className='flex flex-col min-w-0'>
                                                <span className='text-[10px] font-bold text-gray-800 leading-tight truncate max-w-[180px]'>
                                                    {batch.productName}
                                                    {batch.variantName ? ` (${batch.variantName})` : ''}
                                                </span>
                                                <div className='flex items-center gap-2 mt-0.5'>
                                                    <span className='text-[10px] font-medium text-indigo-600 bg-indigo-50 px-1.5 rounded'>
                                                        {batch.batchName}
                                                    </span>
                                                    {batch.costingDate && (
                                                        <span className='text-[9px] font-semibold text-gray-400'>
                                                            {moment(batch.costingDate).format('DD-MM-YYYY')}
                                                        </span>
                                                    )}
                                                    {batch.shiftName && (
                                                        <span className='text-[9px] font-semibold text-gray-400'>
                                                            {batch.shiftName}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedBatches([]);
                                                    setProductId(null);
                                                    setState(prev => ({
                                                        ...prev,
                                                        selectedTimeType: 'WORK_ORDER',
                                                    }));
                                                    setDrillDownView({
                                                        label: 'Actual vs Planned',
                                                        icon: <Waypoints size={16} />,
                                                        value: 'sectionDetails',
                                                        show: true,
                                                        data: [],
                                                        selectedValue: null,
                                                    });
                                                }}
                                                className='p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-all'
                                            >
                                                <span className='text-xs leading-none font-bold'>×</span>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                    <BatchPerformanceHandler
                        isActive={isBatchPerformanceActive}
                        isSingleSelect
                        isSingleDate
                        filters={{
                            dateRange: {
                                from: state?.selectedDate,
                                to: state?.selectedDate,
                                active: true
                            }
                        }}
                        overrideDateRange={overrideDateRange}
                        parentSelectedBatches={selectedBatches}
                        onBatchSelect={(batch, date) => {
                            if (batch && batch.length > 0) {
                                const selectedBatch = batch[0];
                                setProductId(selectedBatch.productId);
                                setSelectedBatches(batch);
                                setState(prev => ({
                                    ...prev,
                                    selectedTimeType: 'PRODUCTION_PLAN',
                                }));
                            }
                        }}
                        onCloseModal={(hasSelection, batches) => {
                            setIsBatchPerformanceActive(false);
                            dispatch(modalActions.toggleCustomModal(false));
                            if (!batches || batches.length === 0) {
                                setIsProductJourneyActive(false);
                                setProductId(null);
                                setSelectedBatches([]);
                                setState(prev => ({
                                    ...prev,
                                    selectedTimeType: 'WORK_ORDER',
                                }));
                                setDrillDownView({
                                    label: 'Actual vs Planned',
                                    icon: <Waypoints size={16} />,
                                    value: 'sectionDetails',
                                    show: true,
                                    data: [],
                                    selectedValue: null,
                                });
                            }
                        }}
                    />
                </DrillDownButtons>
            </div >

            {/* Main Content */}
            {
                viewMode === 'simple' ? (
                    <div style={{
                        // Non-fullscreen: let the section grow with its content so the
                        // page/outer container scrolls as one — no nested inner scroll.
                        height: fullMode ? '100vh' : 'auto',
                        minHeight: '300px',
                        ...(fullMode ? { maxHeight: '100vh' } : {}),
                    }}>
                        <FilterNodes
                            data={filterNodes}
                            handlers={filterNodeHandlers}
                            isProductJourney={drillDownView?.value === 'productJourney'}
                            // Drop the internal overflow/full-height in normal mode so the
                            // cards flow into the outer scroll; keep it for fullscreen.
                            containerClassName={fullMode ? 'w-full h-full overflow-auto p-4 mt-14' : 'w-full p-4 mt-14'}
                        />
                    </div>
                ) : (
                    <FlowChart
                        toggleProcessModal={toggleProcessModal}
                        isInternal={isInternal}
                        isEdit={isEdit}
                        initialState={{ ...initialState, nodes: filterNodes }}
                        handleNodesState={handleNodesState}
                        poActionHandler={poActionHandler}
                        setIsChanged={setIsChanged}
                        toggleImageModal={toggleImageModal}
                        toggleEditNodeModal={toggleEditNodeModal}
                        toggleAlert={toggleAlert}
                        handleEdgeDelete={handleEdgeDelete}
                        handleNodeDelete={handleNodeDelete}
                        selectedStore={selectedStore}
                        newProcessFlow={true}
                    />
                )
            }

            {/* Top Right Settings */}
            <div className='absolute top-[0.5rem] right-[1rem] z-[999] flex gap-3 items-center'>
                {isInternal && level !== 1 && (
                    <>
                        {isChanged && (
                            <div className='animate-pulse flex gap-2'>
                                <Button variant='secondary' onClick={handleCancel}>Cancel</Button>
                                <Button className='primary-btn' onClick={handleSave}>Confirm</Button>
                            </div>
                        )}
                        <Tooltip title='Settings'>
                            <div onClick={handleSettingsClick} className='text-white w-12 h-12 bg-blue-600 rounded-full flex justify-center items-center shadow-lg transition-transform hover:scale-110 active:scale-95'>
                                <Settings />
                            </div>
                        </Tooltip>
                    </>
                )}
                <Tooltip title={viewMode === 'graph' ? 'Switch to Simple View' : 'Switch to Graph View'}>
                    <div
                        onClick={() => setViewMode(prev => prev === 'graph' ? 'simple' : 'graph')}
                        className='text-white w-12 h-12 bg-amber-500 rounded-full flex justify-center items-center cursor-pointer shadow-lg transition-transform hover:scale-110 active:scale-95'
                    >
                        {viewMode === 'graph' ? <LayoutList size={20} /> : <Waypoints size={20} />}
                    </div>
                </Tooltip>
                <Tooltip title={fullMode ? 'Exit Full Screen' : 'View Full Screen'}>
                    <div onClick={handleFullMode} className='text-white w-12 h-12 bg-teal-600 rounded-full flex justify-center items-center cursor-pointer shadow-lg transition-transform hover:scale-110 active:scale-95'>
                        {fullMode ? <Minimize size={18} /> : <Maximize size={18} />}
                    </div>
                </Tooltip>
            </div>
        </div >
    );
};

export default ProcessFlowMiddleContainer;